/**
 * Every line the shell says about money (D165, D166) — the wall's two
 * buttons, the checkout's refusals and arrivals, and the account's own
 * pass — in one place, named by the fact it answers, so the words are the
 * design lane's to re-cut without touching the wiring. The register is the
 * account control's (D157 addendum): plain, second person, the chrome's
 * voice.
 *
 * **`pass-copy.ts` folded in here (D166, and it was not only tidiness).**
 * The split had already cost something: `PASS_ACTION.get` was a second,
 * hand-written copy of {@link passLabelFor}'s no-intro form, so the account
 * dialog showed an intro-eligible account the LIST price in two places
 * while the wall showed it the intro — the same account, the same product,
 * two screens, two prices, and neither screen wrong on its own. One file
 * is not a filing preference; it is the only way "the number on the button
 * is the number Stripe will charge" can be checked by reading.
 *
 * The paywall's own words are NOT here: those are the pack's (`paywall` in
 * the content), spoken in the fiction's voice at the turn that would cross.
 * What is here is the one button that is the platform's, and what the
 * chrome says when a checkout could not start or when the browser comes
 * back from one.
 *
 * **The division is the D157 addendum's, and it is sharper here than it is
 * at the account door.** There, a fictional facility in charge of a real
 * credential was a category error and a phishing costume. Here the fiction
 * may speak — EURYDICE declines to carry the player further, which is a
 * true thing about the game — because it never touches the transaction:
 * the pack names no price, asks for nothing, and collects nothing. Money
 * is said in exactly one place, and that place is this file.
 *
 * **Nothing was charged, wherever the player cannot tell.** Every refusal
 * here is a refusal to START a checkout, and a Stripe Checkout Session is
 * not a charge — no card is touched until the player submits Stripe's own
 * page. So the reassurance is guaranteed by the failure mode rather than
 * hoped for. It is spent only on the refusals that are OPAQUE from the
 * player's chair; saying it after "sign in first" would plant a worry that
 * sentence does not raise.
 *
 * @packageDocumentation
 */

import type { PassDescription, PassHolding, SalePrice } from '@orpheus/api-contracts';

import type { CheckoutRefusal, PurchaseArrival } from './store-client.js';

/** The price as the button shows it — the browser's own formatting for the currency. */
export const formatPrice = (sale: SalePrice, locale = 'en-US'): string =>
	new Intl.NumberFormat(locale, { currency: sale.currency.toUpperCase(), style: 'currency' }).format(sale.priceCents / 100);

/** The date as a line shows it — the browser's own formatting. */
const formatDate = (iso: string, locale = 'en-US'): string => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));

/**
 * The Buy button.
 *
 * @remarks
 * "Buy the game" and not "Buy", because the button has to survive company.
 * D165c adds the pass beside it, and the moment there are two, a bare "Buy"
 * is the one that does not say what it bought — a player reading "Buy —
 * $9.99" next to a subscription cannot tell which of the two this is. The
 * noun costs two words now and prevents the ambiguity later.
 *
 * The component has no room for more: three slots, and this is the whole
 * of one. That this purchase is outright and permanent is a fact the label
 * cannot carry, and it is why the word is "game" rather than "access".
 *
 * @param sale - The price, absent on a game with none
 * @returns The label
 */
export const buyLabelFor = (sale: SalePrice | undefined): string => (sale === undefined ? 'Buy the game' : `Buy the game — ${formatPrice(sale)}`);

/**
 * The pass button (D165c), beside Buy on a game the pass covers.
 *
 * @remarks
 * Two facts the label may not drop: that this recurs, and when the intro
 * ends. It sits beside "Buy the game", which is permanent, and a player
 * reading both must be able to tell which is which.
 *
 * **"a month" now sits on BOTH numbers.** The intro form used to read
 * "$9.99 a month for 3 months, then $14.99" — leaving the price they will
 * actually pay for most of the subscription's life as a bare number, while
 * the cheap one carried the recurrence. Parallelism arguably carries it
 * over; that is not good enough here, because putting the period on the
 * discount and omitting it from the list price is the precise silhouette
 * of the trick this label exists not to be. The rule is stronger than the
 * reading: every price on this button says what it repeats.
 *
 * "for 3 months" is enough without "your first 3 months" — the store shows
 * an intro only to an account eligible for one (Mike's ruling: first pass
 * only, and the store rather than the coupon enforces it), so the sentence
 * is true for everyone who can see it.
 *
 * @param pass - The pass as the store describes it
 * @returns The label
 */
export const passLabelFor = (pass: PassDescription): string =>
	pass.intro === undefined
		? `Get the pass — ${formatPrice(pass)} a month`
		: `Get the pass — ${formatPrice({ currency: pass.currency, priceCents: pass.intro.priceCents })} a month for ${String(pass.intro.months)} month${pass.intro.months === 1 ? '' : 's'}, then ${formatPrice(pass)} a month`;

/**
 * What the chrome says when a checkout could not start.
 *
 * @remarks
 * Six of these ten are faults the player did not cause and cannot act on,
 * and they collapse into one sentence on purpose: distinguishing
 * `not_json` from `bad_signature` on screen would tell a player about our
 * wiring and give them nothing to do about it. What they need is what
 * happened, that their card is untouched, and whether trying again is
 * worth it.
 *
 * `not_signed_in` is the only one most players will ever meet, and it is
 * the only one that names a REASON rather than a fault — because there is
 * something to understand: the account control sits in the same row, so
 * the sentence points at a thing on screen, and the "not to this browser"
 * half is why signing in is worth the interruption rather than a hoop.
 *
 * **This table serves TWO flows** since D165b: buying a game, and a
 * creator onboarding to sell one. `CheckoutRefusal` is the union of both
 * doors' errors, so a key must be written for whichever flow can actually
 * REACH it — a player buying never sees `not_an_onboard_request`, and a
 * creator onboarding never sees `game_not_for_sale`. That matters here
 * because "nothing was charged" is a sentence about a purchase: said
 * during onboarding, where no payment was ever in flight, it invents a
 * worry out of nothing. So `not_an_onboard_request` gets the plain
 * apology and no money in it.
 *
 * `creator_not_payable` is the one place "try again in a moment" would be
 * a LIE. Stripe will not release funds to that account until its owner
 * finishes onboarding, which may be never and is certainly not a moment —
 * so the line says retrying will not help, which is the only useful thing
 * anyone can tell the player standing there.
 */
export const CHECKOUT_REFUSED: Readonly<Record<CheckoutRefusal, string>> = {
	bad_signature: 'Something went wrong starting the checkout. Nothing was charged — try again in a moment.',
	creator_not_payable: 'This game cannot take payments right now. Nothing was charged, and trying again will not help until that changes.',
	game_not_for_sale: 'This game is not for sale here.',
	game_not_found: 'This game is not for sale here.',
	not_a_checkout_request: 'Something went wrong starting the checkout. Nothing was charged — try again in a moment.',
	// D168: the creator's statement door refuses with these; the studio reads that door, never this shell, so they are
	// here because the table is total. PLACEHOLDERS (Lyra) — Calder's, if a shell ever shows them.
	not_a_creator: 'This account is not set up as a creator.',
	not_a_month: 'That is not a month.',
	// D241: the Record doors refuse with this; the shell meets it only if its own request is malformed, which no player
	// can cause. Here because the table is total. PLACEHOLDER (Lyra) — Calder's, if a shell ever shows it.
	not_a_record_request: 'Something went wrong. Nothing was charged — try again in a moment.',
	// D165c: an internal door's refusal, never a browser's — here because the table is total, said plainly in case.
	not_an_engagement: 'Something went wrong. Nothing was charged — try again in a moment.',
	not_an_onboard_request: 'Something went wrong starting that. Try again in a moment.',
	not_found: 'Something went wrong starting the checkout. Nothing was charged — try again in a moment.',
	not_json: 'Something went wrong starting the checkout. Nothing was charged — try again in a moment.',
	// D165c: this one line now serves BOTH buttons, so it cannot name either product. "A game you buy" was true of Buy
	// and wrong of a subscription; "what you buy" is the smallest phrase that covers a game and a pass at once, and the
	// argument the sentence exists to make — the thing outlives the browser — is what makes signing in worth the
	// interruption for either.
	not_signed_in: 'Sign in first — what you buy belongs to your account, not to this browser.',
	// D165c: reachable only by a race — the button is offered on a pass game, a holder is not stopped at one, so the
	// subscription landed between the render and the click. "Not charged AGAIN" is the whole point of the re-cut: this is
	// the one refusal where a player has real cause to fear a DOUBLE charge, and it is exactly the opaque case the
	// reassurance is reserved for.
	pass_already_held: 'You already have the pass — you were not charged again. Type what you were going to type.',
	// D165c: this deployment has no pass, so the button should not have rendered. Naming our fault is not self-flagellation;
	// without it the player retries a button that cannot work.
	pass_not_offered: 'The pass is not available. Nothing was charged — the button should not have been offered.',
	return_url_not_allowed: 'A checkout cannot be started from this page.',
	stripe_unavailable: 'Payments are not being taken right now. Nothing was charged — try again shortly.',
	unreachable: 'The store could not be reached. Nothing was charged — check your connection and try again.',
};

/**
 * What the chrome says when the browser comes back from a checkout.
 *
 * @remarks
 * **"The way is open" was the one sentence here that could be false.** The
 * entitlement arrives by webhook and a fast return beats it, so the words
 * ran ahead of the fact — and the old line's own hedge ("type what you
 * were going to type") came AFTER the promise, which is the port notice's
 * defect exactly: state the thing that is not yet reliably true, then
 * qualify it. A player who reads a promise and then meets the paywall
 * again has been lied to, however carefully the second clause was written.
 *
 * The purchase and the entitlement are two facts, and only the first is
 * certain at this moment. So the line asserts the certain one — the game
 * IS theirs; Stripe said so — and names the other as a delay rather than a
 * risk. "The receipt catching up" is the whole webhook race in five words
 * a player already has, and it turns a second refusal from a betrayal into
 * something they were told to expect.
 *
 * `canceled` is Lyra's, unchanged. "Nothing was charged" is the only thing
 * anyone wants to know after backing out of a payment, and it says it in
 * three words.
 */
export const PURCHASE_ARRIVAL: Readonly<Record<PurchaseArrival, string>> = {
	canceled: 'Checkout canceled. Nothing was charged.',
	completed: 'Thank you — the game is yours. Type what you were going to type; if it stops you once more, that is the receipt catching up.',
	// D165c: Lyra's, and RULED AS WRITTEN rather than re-cut. She applied `completed`'s rule herself and got the certain
	// fact right — the pass is not one game, so "the game is yours" would be false, and "you have the pass" is what
	// Stripe actually confirmed. The second sentence is `completed`'s verbatim and true here for the identical reason.
	// `pass_canceled` is `canceled` word for word because backing out of a subscription checkout and backing out of a
	// purchase leave the player with the same single question. Two lines that should match, matching.
	pass_canceled: 'Checkout canceled. Nothing was charged.',
	pass_completed: 'Thank you — you have the pass. Type what you were going to type; if it stops you once more, that is the receipt catching up.',
};

/**
 * What the pass covers, under the wall's two buttons (D166).
 *
 * @remarks
 * Beside "Buy the game — $14.99", "Get the pass — $14.99 a month" reads as
 * strictly WORSE unless something on the screen says the pass is more than
 * this game: same money, and one of them recurs. That is the whole job of
 * this line, and it is why the note is shown only when the pass button is.
 *
 * "Every other game on it" and never "every game" — the pass covers the
 * games published to it, not the catalog, and the promises law does not
 * stop at the edge of the fiction. It also stops short of promising a LIST:
 * there is no game selector yet, so a sentence inviting the player to go
 * and browse would be a door that opens on nothing.
 */
/**
 * What the pass covers, under the wall's two buttons (D166, D167).
 *
 * @remarks
 * Beside "Buy the game — $14.99", "Get the pass — $14.99 a month" reads as
 * strictly WORSE unless something on the screen says the pass is more than
 * this game: same money, and one of them recurs. That is the whole job of
 * this line, and it is why the note appears only when the pass button does.
 *
 * **The count, and never "every game."** The pass covers what is published
 * to it, not the catalog, and the promises law does not stop at the edge
 * of the fiction. A number is also the only form of the answer with no
 * room left to doubt it — and it is now honest to print, because the store
 * offers the pass at all only once it covers two, so the sentence can
 * never be the thin "one other game" it would have been last week.
 *
 * **Digits, deliberately departing from `port-copy.ts`'s words-below-ten
 * rule.** There an ordinal names a POSITION inside a sentence a player is
 * reading, and "the second of your 43" reads better spelled. Here the
 * number is a MAGNITUDE the player is being asked to weigh against a
 * one-game purchase — the whole argument of the sentence — and "40 games"
 * lands where "forty games" is scanned past. It also has to survive forty
 * and four hundred without the rule changing under it.
 *
 * The plural is mechanical rather than a branch: the store guarantees two
 * or more, so "1 game" is unreachable — but a sentence that would be
 * BROKEN if the guarantee ever moved is a worse bet than one character.
 *
 * @param pass - The pass as the store describes it
 * @returns The note
 */
export const passNoteFor = (pass: PassDescription): string =>
	`The pass covers ${String(pass.games.length)} game${pass.games.length === 1 ? '' : 's'}, including this one.`;

/** To Stripe's Customer Portal — cancel, resume, change the card, read the invoices. */
export const PASS_MANAGE_LABEL = 'Manage the pass';

/**
 * The line for the account's own pass, by what the store said (D166).
 *
 * @remarks
 * **No price is said here, and that is the point.** Every one of these
 * lines is rendered beside a button that already carries the price —
 * {@link passLabelFor}, which knows about the intro — so a second number
 * in the sentence can only ever agree by luck or disagree by drift. It
 * disagreed by drift once already (see this file's header). The button is
 * where money lives; the line says what is TRUE of the account.
 *
 * A subscription set to end still reads "you have the pass until…",
 * because that is what the player paid for and still has. "Ended" is the
 * word for after.
 *
 * `past_due` is the one state naming a fault that is not ours: the card
 * failed and Stripe is retrying, and the pass is not held meanwhile — a
 * month that is not paid for is not a month. The line points at Manage
 * and never at a second checkout (Lyra's rule), which the panel honors:
 * `past_due` routes to the portal, so "under Manage" names a button that
 * is actually on screen. It promises nothing about WHEN a fixed card
 * revives the pass, because that is Stripe's retry schedule and not ours
 * to pledge.
 *
 * @param holding - The account's own pass; `null` for one that never held it
 * @param pass - Read for its PRESENCE only, now that the price lives on the button: whether this deployment sells a pass at all
 * @returns The line; `undefined` when there is nothing true to say (no pass held and none offered)
 */
export const passLineOf = (holding: PassHolding, pass: PassDescription | undefined): string | undefined => {
	if (holding === null) {
		return pass === undefined ? undefined : 'The pass covers every game on it.';
	}

	if (holding.isHeld) {
		if (holding.periodEndsAtIso === undefined) {
			return 'You have the pass.';
		}

		if (holding.isCancelingAtPeriodEnd) {
			return `You have the pass until ${formatDate(holding.periodEndsAtIso)}. It will not renew.`;
		}

		/*
		 * D167: the intro's end is the one date a subscriber comes here to check. The "first price with no end on
		 * it" shape was kept off the button across two rounds; it must not reappear on the screen they open
		 * specifically to find out what they are paying.
		 *
		 * No amount, and here for a HARDER reason than the rule elsewhere in this function. A holder's button is
		 * "Manage the pass", not a price — `use-pass-panel` asks the store for the price only when a checkout will
		 * need it, so for a subscriber there is no number anywhere on the screen for a sentence to agree with or
		 * contradict. That makes "it costs more" the whole of what can honestly be said, which is why the line ends
		 * by naming where the amount actually lives instead of leaving the reader to hunt for it.
		 */
		return holding.introEndsAtIso === undefined
			? `You have the pass — it renews on ${formatDate(holding.periodEndsAtIso)}.`
			: `You have the pass — it renews on ${formatDate(holding.periodEndsAtIso)}. The introductory price runs until ${formatDate(holding.introEndsAtIso)}; after that the pass costs more, and Manage shows how much.`;
	}

	return holding.status === 'past_due'
		? 'Your last pass payment did not go through, so the pass is paused. You can change the card under Manage.'
		: 'Your pass has ended.';
};
